import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, FileText, Receipt, Users, TicketIcon, LogOut, Settings, UsersIcon, Building2, Calendar, Menu, X, Clock, CalendarDays, Banknote, ShieldCheck, Mail, BookOpen, Zap, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

export const Layout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyConfig, setCompanyConfig] = useState(null);

  useEffect(() => {
    fetchCompanyConfig();
  }, []);

  const fetchCompanyConfig = async () => {
    try {
      const response = await axiosInstance.get('/company-config');
      setCompanyConfig(response.data);
      
      // Apply corporate color to CSS variables
      if (response.data.corporate_color) {
        document.documentElement.style.setProperty('--corporate-color', response.data.corporate_color);
      }
    } catch (error) {
      console.error('Error loading company config');
    }
  };

  // Get logo to display (company logo or default OSAGEST)
  const logoSrc = companyConfig?.company_logo || '/logos/osagest-logo-white.png';
  const corporateColor = companyConfig?.corporate_color || '#0066B3';

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Sesión cerrada correctamente');
    navigate('/login');
  };

  // Los permisos tienen que ser EXACTAMENTE los que comprueba el backend en
  // require_permission(). Antes habia aqui 'productos', 'presupuestos',
  // 'facturas', 'clientes' y 'calendario', que el backend no mira: al marcarlos
  // aparecia la entrada de menu y luego la pantalla daba 403 en todo, porque
  // esos endpoints piden 'facturacion' o 'crm'.
  //
  // Los reales son: facturacion, crm, ticketing, teamup, plantillas_email.
  // 'admin' no es un permiso, es el rol (lo resuelve hasPermission).
  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: null },
    { path: '/products', icon: Package, label: 'Productos', permission: 'facturacion' },
    { path: '/quotes', icon: FileText, label: 'Presupuestos', permission: 'facturacion' },
    { path: '/invoices', icon: Receipt, label: 'Facturas', permission: 'facturacion' },
    { path: '/sepa-remittances', icon: Banknote, label: 'Remesas SEPA', permission: 'facturacion' },
    { path: '/verifactu', icon: ShieldCheck, label: 'Verifactu', permission: 'admin' },
    { path: '/email-config', icon: Mail, label: 'Email', permission: 'admin' },
    { path: '/contabilidad/fiscal', icon: BookOpen, label: 'Contabilidad', permission: 'admin' },
    { path: '/clients', icon: Building2, label: 'Clientes', permission: 'crm' },
    { path: '/proveedores', icon: Building2, label: 'Proveedores', permission: 'admin' },
    { path: '/tickets', icon: TicketIcon, label: 'Tickets', permission: 'ticketing' },
    { path: '/teamup', icon: CalendarDays, label: 'Teamup', permission: 'teamup' },
    { path: '/crm', icon: Users, label: 'CRM', permission: 'crm' },
    { path: '/campaigns', icon: Target, label: 'Campañas', permission: 'crm' },
    { path: '/partners', icon: Building2, label: 'Partners', permission: 'admin' },
    { path: '/settlements', icon: Banknote, label: 'Liquidaciones', permission: 'admin' },
    { path: '/workflows', icon: Zap, label: 'Workflows', permission: 'admin' },
    { path: '/calendar', icon: Calendar, label: 'Calendario', permission: 'crm' },
    { path: '/fichar', icon: Clock, label: 'Fichar', permission: null },
    { path: '/mis-registros', icon: CalendarDays, label: 'Mis registros', permission: null },
    { path: '/equipo-fichajes', icon: Users, label: 'Registro de jornada', permission: 'admin' },
  ];

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!permission) return true;
    return user.permissions?.[permission] === true;
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="layout-container">
      {/* Mobile Header */}
      <div className="md:hidden bg-[var(--osagest-gray-dark)] text-white p-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src={logoSrc} alt="Logo" className="h-8 w-auto max-w-32 object-contain" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white hover:bg-white/10"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar Desktop */}
      <aside className="hidden md:block w-64 bg-[var(--osagest-gray-dark)] text-white shadow-2xl">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <img src={logoSrc} alt="Logo" className="h-8 w-auto max-w-48 object-contain" />
          </div>
          {user && (
            <p className="text-xs text-white/70 mt-1" data-testid="user-name">Hola, {user.name}</p>
          )}
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const canAccess = hasPermission(item.permission);

            if (!canAccess) return null;

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                  isActive
                    ? 'text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                style={isActive ? { backgroundColor: corporateColor } : {}}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-64 p-3 border-t border-white/10 space-y-1">
          {user?.permissions?.crm && (
            <Link to="/settings" data-testid="nav-settings">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white h-8 text-xs"
              >
                <Settings className="w-4 h-4 mr-2" />
                Config. CRM
              </Button>
            </Link>
          )}
          {user?.role === 'admin' && (
            <>
              <Link to="/users" data-testid="nav-users">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white h-8 text-xs"
                >
                  <UsersIcon className="w-4 h-4 mr-2" />
                  Usuarios
                </Button>
              </Link>
              <Link to="/company-config" data-testid="nav-config">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white h-8 text-xs"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configuración
                </Button>
              </Link>
            </>
          )}
          {user?.permissions?.plantillas_email && (
            <Link to="/send-templates" data-testid="nav-send-templates">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white h-8 text-xs"
              >
                <Mail className="w-4 h-4 mr-2" />
                Plantillas de Envío
              </Button>
            </Link>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-red-300 hover:bg-red-900/20 hover:text-red-200 h-8 text-xs"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <aside
            className="w-64 bg-[var(--osagest-gray-dark)] text-white shadow-2xl h-full overflow-y-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-3 mb-2">
                <img src={logoSrc} alt="Logo" className="h-10 w-auto max-w-48 object-contain" />
              </div>
              {user && (
                <p className="text-sm text-white/70 mt-2">Hola, {user.name}</p>
              )}
            </div>

            <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const canAccess = hasPermission(item.permission);

                if (!canAccess) return null;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                    style={isActive ? { backgroundColor: corporateColor } : {}}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 border-t border-white/10 space-y-2 flex-shrink-0">
              {user?.permissions?.crm && (
                <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <Settings className="w-5 h-5 mr-3" />
                    Config. CRM
                  </Button>
                </Link>
              )}
              {user?.role === 'admin' && (
                <>
                  <Link to="/users" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      <UsersIcon className="w-5 h-5 mr-3" />
                      Usuarios
                    </Button>
                  </Link>
                  <Link to="/company-config" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      <Settings className="w-5 h-5 mr-3" />
                      Configuración
                    </Button>
                  </Link>
                </>
              )}
              {user?.permissions?.plantillas_email && (
                <Link to="/send-templates" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <Mail className="w-5 h-5 mr-3" />
                    Plantillas de Envío
                  </Button>
                </Link>
              )}
              <Button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                variant="ghost"
                className="w-full justify-start text-red-300 hover:bg-red-900/20 hover:text-red-200"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 sm:p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
